import React from 'react';
import './NSWBackgroundWatermark.css';

interface NSWBackgroundWatermarkProps {
  referenceNumber?: string;
  entityType?: string;
}

const NSWBackgroundWatermark: React.FC<NSWBackgroundWatermarkProps> = ({ 
  referenceNumber = 'CDD-001',
  entityType = 'COMPANY',
}) => {
  return (
    <div className="nsw-background-watermark">
      {/* Full page SVG background */}
      <svg
        className="nsw-bg-svg"
        width="100%"
        height="100%"
        viewBox="0 0 1920 1080"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* Main holographic gradient - NSW license style */}
          <linearGradient id="bgHolographic" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6">
              <animate attributeName="stop-color" 
                values="#3B82F6;#9333EA;#EC4899;#F97316;#22C55E;#06B6D4;#3B82F6" 
                dur="8s" repeatCount="indefinite"/>
            </stop>
            <stop offset="25%" stopColor="#9333EA">
              <animate attributeName="stop-color" 
                values="#9333EA;#EC4899;#F97316;#22C55E;#06B6D4;#3B82F6;#9333EA" 
                dur="8s" repeatCount="indefinite"/>
            </stop>
            <stop offset="50%" stopColor="#EC4899">
              <animate attributeName="stop-color" 
                values="#EC4899;#F97316;#22C55E;#06B6D4;#3B82F6;#9333EA;#EC4899" 
                dur="8s" repeatCount="indefinite"/>
            </stop>
            <stop offset="75%" stopColor="#22C55E">
              <animate attributeName="stop-color" 
                values="#22C55E;#06B6D4;#3B82F6;#9333EA;#EC4899;#F97316;#22C55E" 
                dur="8s" repeatCount="indefinite"/>
            </stop>
            <stop offset="100%" stopColor="#06B6D4">
              <animate attributeName="stop-color" 
                values="#06B6D4;#3B82F6;#9333EA;#EC4899;#F97316;#22C55E;#06B6D4" 
                dur="8s" repeatCount="indefinite"/>
            </stop>
          </linearGradient>

          {/* Shimmer gradient */}
          <linearGradient id="bgShimmer" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0)"/>
            <stop offset="45%" stopColor="rgba(255,255,255,0.01)"/>
            <stop offset="50%" stopColor="rgba(255,255,255,0.03)"/>
            <stop offset="55%" stopColor="rgba(255,255,255,0.01)"/>
            <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
          </linearGradient>

        </defs>

        {/* Large center shield watermark */}
        <g className="center-watermark" transform="translate(960, 540)">
          <g transform="translate(-150, -180)">
            {/* Outer shield - large */}
            <path 
              d="M150 20 L280 60 L280 200 Q280 300 150 360 Q20 300 20 200 L20 60 Z"
              fill="none"
              stroke="url(#bgHolographic)"
              strokeWidth="3"
              opacity="0.04"
              className="shield-outer"
            />
            {/* Inner shield */}
            <path 
              d="M150 50 L250 80 L250 190 Q250 270 150 320 Q50 270 50 190 L50 80 Z"
              fill="url(#bgHolographic)"
              opacity="0.02"
              className="shield-inner"
            />
            {/* Checkmark */}
            <path 
              d="M100 180 L140 220 L210 130"
              stroke="url(#bgHolographic)"
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.03"
              className="checkmark"
            />
          </g>
        </g>

        {/* CONFIDENTIAL - Small inclined diagonal watermarks */}
        <g className="confidential-diagonal" opacity="0.04">
          <text 
            x="300" 
            y="200" 
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="system-ui, -apple-system, sans-serif" 
            fontSize="40" 
            fontWeight="700"
            letterSpacing="8"
            fill="url(#bgHolographic)"
            transform="rotate(-30, 300, 200)"
          >
            CONFIDENTIAL
          </text>
          <text 
            x="1620" 
            y="200" 
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="system-ui, -apple-system, sans-serif" 
            fontSize="40" 
            fontWeight="700"
            letterSpacing="8"
            fill="url(#bgHolographic)"
            transform="rotate(-30, 1620, 200)"
          >
            CONFIDENTIAL
          </text>
          <text 
            x="300" 
            y="880" 
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="system-ui, -apple-system, sans-serif" 
            fontSize="40" 
            fontWeight="700"
            letterSpacing="8"
            fill="url(#bgHolographic)"
            transform="rotate(-30, 300, 880)"
          >
            CONFIDENTIAL
          </text>
          <text 
            x="1620" 
            y="880" 
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="system-ui, -apple-system, sans-serif" 
            fontSize="40" 
            fontWeight="700"
            letterSpacing="8"
            fill="url(#bgHolographic)"
            transform="rotate(-30, 1620, 880)"
          >
            CONFIDENTIAL
          </text>
        </g>

        {/* ARCaml brand - MAIN VISIBLE BRANDING - LARGE */}
        <text 
          x="960" 
          y="450" 
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="system-ui, -apple-system, sans-serif" 
          fontSize="320" 
          fontWeight="900"
          fill="url(#bgHolographic)"
          opacity="0.07"
          className="arcaml-watermark"
        >
          ARCaml
        </text>

        {/* CDD VERIFIED SYSTEM - PROMINENT */}
        <text 
          x="960" 
          y="620" 
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="system-ui, -apple-system, sans-serif" 
          fontSize="72" 
          fontWeight="700"
          letterSpacing="20"
          fill="url(#bgHolographic)"
          opacity="0.06"
          className="verified-watermark"
        >
          CDD VERIFIED SYSTEM
        </text>

        {/* Reference number badges scattered */}
        <g className="ref-badges" opacity="0.04">
          <text x="100" y="100" fontFamily="monospace" fontSize="24" fill="url(#bgHolographic)">{referenceNumber}</text>
          <text x="1700" y="150" fontFamily="monospace" fontSize="24" fill="url(#bgHolographic)">{referenceNumber}</text>
          <text x="150" y="950" fontFamily="monospace" fontSize="24" fill="url(#bgHolographic)">{referenceNumber}</text>
          <text x="1650" y="1000" fontFamily="monospace" fontSize="24" fill="url(#bgHolographic)">{referenceNumber}</text>
          <text x="960" y="1040" fontFamily="monospace" fontSize="20" fill="url(#bgHolographic)" textAnchor="middle">{entityType}</text>
        </g>

        {/* Corner decorations */}
        <g className="corner-decorations" opacity="0.05">
          {/* Top left */}
          <path d="M0 0 L100 0 L100 5 L5 5 L5 100 L0 100 Z" fill="url(#bgHolographic)"/>
          {/* Top right */}
          <path d="M1920 0 L1820 0 L1820 5 L1915 5 L1915 100 L1920 100 Z" fill="url(#bgHolographic)"/>
          {/* Bottom left */}
          <path d="M0 1080 L100 1080 L100 1075 L5 1075 L5 980 L0 980 Z" fill="url(#bgHolographic)"/>
          {/* Bottom right */}
          <path d="M1920 1080 L1820 1080 L1820 1075 L1915 1075 L1915 980 L1920 980 Z" fill="url(#bgHolographic)"/>
        </g>

        {/* Animated shimmer overlay */}
        <rect width="100%" height="100%" fill="url(#bgShimmer)" className="shimmer-overlay">
          <animateTransform
            attributeName="transform"
            type="translate"
            from="-100% -100%"
            to="100% 100%"
            dur="8s"
            repeatCount="indefinite"
          />
        </rect>

        {/* Security dots border */}
        <g className="security-dots-border" opacity="0.05">
          {[...Array(48)].map((_, i) => (
            <circle key={`top-${i}`} cx={i * 40 + 20} cy="15" r="4" fill="url(#bgHolographic)"/>
          ))}
          {[...Array(48)].map((_, i) => (
            <circle key={`bottom-${i}`} cx={i * 40 + 20} cy="1065" r="4" fill="url(#bgHolographic)"/>
          ))}
        </g>

      </svg>

      {/* Corner badge - small floating NSW logo */}
      <div className="corner-badge">
        <svg width="100" height="56" viewBox="0 0 220 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="cornerHolo" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6">
                <animate attributeName="stop-color" values="#3B82F6;#9333EA;#EC4899;#3B82F6" dur="4s" repeatCount="indefinite"/>
              </stop>
              <stop offset="100%" stopColor="#9333EA">
                <animate attributeName="stop-color" values="#9333EA;#EC4899;#3B82F6;#9333EA" dur="4s" repeatCount="indefinite"/>
              </stop>
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="216" height="116" rx="10" fill="#0f3460" stroke="url(#cornerHolo)" strokeWidth="2"/>
          <text x="110" y="50" textAnchor="middle" fontFamily="system-ui" fontSize="24" fontWeight="800" fill="white">ARCaml</text>
          <text x="110" y="75" textAnchor="middle" fontFamily="system-ui" fontSize="10" fontWeight="600" fill="rgba(255,255,255,0.7)" letterSpacing="2">CDD VERIFIED</text>
          <rect x="10" y="100" width="200" height="8" rx="4" fill="url(#cornerHolo)" opacity="0.6"/>
        </svg>
      </div>
    </div>
  );
};

export default NSWBackgroundWatermark;
